(() => {
  if (customElements.get('hmg-order-impact')) return;
  class HmgOrderImpact extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.start = () => {
        if (!this.isConnected) return;
        if (!('IntersectionObserver' in window)) return this.schedule();
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.observer.disconnect();
            this.schedule();
          }
        }, { rootMargin: '120px 0px' });
        this.observer.observe(this);
      };
      if (document.readyState === 'complete') this.start();
      else window.addEventListener('load', this.start, { once: true });
    }
    schedule() {
      if (this.scheduled) return;
      this.scheduled = true;
      if ('requestIdleCallback' in window) this.idle = requestIdleCallback(() => this.load(), { timeout: 2000 });
      else this.timer = setTimeout(() => this.load(), 250);
    }
    async load() {
      if (!this.isConnected) return;
      this.dataset.state = 'loading';
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(this.dataset.shop)) throw new Error('Invalid shop');
        const uri = new URL('https://sprout-app.thegoodapi.com/app/badges/product');
        const variant = this.closest('.product__info-container')?.querySelector('product-form input[name="id"]')?.value || this.dataset.variantId;
        uri.search = new URLSearchParams({
          shop: this.dataset.shop, currency_symbol: '$', currency_code: this.dataset.currency || 'AUD',
          rate: '1', product_id: this.dataset.productId, tags: this.dataset.tags || '',
          local_topup_price: '-1', current_variant_id: variant || '', block_type: 'product', locale: 'en', cdn_key: ''
        });
        const response = await fetch(uri, { credentials: 'omit', signal: controller.signal });
        if (!response.ok) throw new Error('Badge unavailable');
        const markup = await response.text();
        if (markup.length > 100000) throw new Error('Unexpected response');
        // Extract text from the official badge only. Never execute or insert remote markup.
        const inert = document.createElement('template');
        inert.innerHTML = markup;
        const content = inert.content.querySelector('.sprout-product-badge-content')?.textContent.replace(/\s+/g, ' ').trim();
        const amounts = /^Every order plants (\d+) trees? & removes (\d+) plastic bottles?$/i.exec(content || '');
        if (!amounts) throw new Error('No matching per-order contribution');
        const trees = Number(amounts[1]);
        const bottles = Number(amounts[2]);
        if (![trees, bottles].every(n => Number.isSafeInteger(n) && n > 0 && n <= 100000)) throw new Error('Invalid quantities');
        if (controller.signal.aborted || !this.isConnected || this.controller !== controller) return;
        this.querySelector('[data-impact-statement]').textContent = `Each order plants ${trees === 1 ? 'one' : trees} ${trees === 1 ? 'tree' : 'trees'} and removes ${bottles === 2 ? 'two' : bottles} plastic ${bottles === 1 ? 'bottle' : 'bottles'} from the ocean.`;
        this.dataset.state = 'ready';
      } catch (_) {
        if (this.isConnected && this.controller === controller) {
          this.querySelector('[data-impact-statement]').textContent = 'Supporting tree planting and plastic recovery';
          this.dataset.state = 'unavailable';
        }
      } finally {
        clearTimeout(timeout);
      }
    }
    disconnectedCallback() {
      window.removeEventListener('load', this.start);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      if (this.idle !== undefined && 'cancelIdleCallback' in window) cancelIdleCallback(this.idle);
      clearTimeout(this.timer);
      this.started = false;
      this.scheduled = false;
    }
  }
  customElements.define('hmg-order-impact', HmgOrderImpact);
})();
